import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { offerIncomingPrinterProfile, offerIncomingScan, printerProfileIdFromUrl } from '../features/scan/incoming';
import { isNative } from './host';

/**
 * The parts of the Android application that are not a web page.
 *
 * Renders nothing. It exists to hold the two things a WebView has that a
 * browser tab does not: a hardware Back key, and labels that open the
 * application itself instead of a browser.
 *
 * Everything is loaded on demand, so the browser build never carries any of it.
 */

/** The code inside a label's address, whichever form it arrives in.
 *
 *  `https://storage.m8s.ru/r/C-AHYD15` is what is printed on the tape;
 *  `?scan=` is what the server's redirect turns it into, and an Android
 *  intent filter can match either. */
export function codeFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    const scan = parsed.searchParams.get('scan');
    if (scan) return scan;
    const match = /^\/r\/(.+)$/.exec(parsed.pathname);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/** A saved TSPL configuration printed on the final calibration label. */
export function printerProfileFromUrl(url: string): string | null {
  return printerProfileIdFromUrl(url);
}

export function NativeShell() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return undefined;

    let stop = () => {};
    let cancelled = false;

    void (async () => {
      const [{ App }, { StatusBar }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/status-bar'),
      ]);
      if (cancelled) return;

      // The page keeps its own strip of screen: drawn underneath the status
      // bar, the first row of every list would sit behind the clock.
      void StatusBar.setOverlaysWebView({ overlay: false });

      const back = await App.addListener('backButton', ({ canGoBack }) => {
        // Back walks the history it built, exactly as in a browser. At the
        // first screen there is nothing behind, and on Android a Back key that
        // does nothing is a application that cannot be left.
        if (canGoBack) window.history.back();
        else void App.exitApp();
      });

      const opened = await App.addListener('appUrlOpen', ({ url }) => {
        const printerProfile = printerProfileFromUrl(url);
        if (printerProfile) {
          offerIncomingPrinterProfile(printerProfile);
          navigate('/print');
          return;
        }
        const code = codeFromUrl(url);
        if (!code) return;
        // Held rather than acted on: the same path a printed label takes in a
        // browser, and the one that survives being asked to sign in first.
        offerIncomingScan(code);
        navigate('/scan');
      });

      stop = () => {
        void back.remove();
        void opened.remove();
      };
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [navigate]);

  return null;
}
